/* eslint-disable css-modules/no-unused-class */
import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';
import { CircularProgress } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { FormApi, FormState } from 'final-form';
import { FormSpy } from 'react-final-form';
import Form from 'ui/Form/Form';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import Button, { Themes as ButtonThemes, Sizes as ButtonSizes } from 'ui/Button/Button';
import { requiredOnly, validationCallback } from 'utils/validation';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { AxiosStatus } from 'api/axiosConfig';
import routePaths from 'routes/routePaths';
import { NETWORK_REQUIRED } from 'utils/constants';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Table from 'ui/Table/Table';
import noop from 'utils/noop';
import { getFileSize } from 'utils/formatters';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import { IWalletState } from '../ManualEntry/ManualEntry.model';
import columns from './columns';
import {
  FormType,
  IBulkDataList,
  IBulkUploadData,
  IFormValues,
  IMobileMoneyResponse,
  ITransactionField,
  bulkDataConfig,
  bulkSortArray,
} from './MMFileUpload.model';
import { renderBulkFileUpload, renderTransferFieldsFrom } from './MMFileUploadField';

import styles from './BuMMFileUpload.module.css';

import trash from 'assets/icons/trash.png';
import excel from 'assets/icons/excel.png';
import download from 'assets/img/download.png';

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  file: requiredOnly('File upload'),
});
interface IProps {
  accounts: IWalletState['accounts'];
  bulkTransfersState: 'adding' | 'confirmation' | 'report';
  // eslint-disable-next-line react/no-unused-prop-types
  openAddBulkTransfersForm?: () => void;
  showMainForm: boolean;
  handleChangeForm: () => void;
}

const MMFileUpload = ({ accounts, bulkTransfersState, showMainForm, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [tableData, setTableData] = useState<IBulkDataList[]>([]);
  const [mainData, setMainData] = useState<IBulkDataList[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [disableBulkTransfer, setDisableMakeTransfer] = useState<boolean>(false);
  const [elevyState, setElevyState] = useState<number>(0);
  const history = useHistory();
  const emptyMessage = 'Bulk list will be displayed here.';
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };
  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };
  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;
  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);
  const onChangeForm = useCallback(
    ({ dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
    },
    [handleChangeForm],
  );

  const getPrincipalAmount = () => {
    const transferList = mainData;
    if (transferList.length > 0) {
      const result = transferList.map((item) => Number(item.amount)).reduce((a, b) => a + b, 0);
      return result;
    }
    return 0;
  };


  const getPrincipalTransactionCharge = () => {
    const transferList = mainData;
    if (transferList.length > 0) {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const result = transferList.map((item) => Number(item.charge)).reduce((a, b) => a + b, 0);
      return result;
    }
    return 0;
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
  }, []);

  const BulkTransfersConfirmation = () => {
    const { sourceAccount } = mainForm.getState().values;
    const readyValues = {
      sourceAccount,
      numberOfTransactions: mainData.length,
      principalAmount: getPrincipalAmount(),
      ELevyFee: elevyState,
      TransactionFee: getPrincipalTransactionCharge(),
      totalAmount: Number(getPrincipalAmount() + (elevyState) + getPrincipalTransactionCharge()),
    };
    const SelectedAccount = accounts.find((account) => account.accountNumber === sourceAccount);
    if (SelectedAccount) {
      const { availableBalance } = SelectedAccount;
      if (readyValues.totalAmount > availableBalance) {
        setSubmitStatus(AxiosStatus.FAILURE);
        setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
        return;
      }
    }
    setPreviewData(getTransactionsArray(readyValues, bulkDataConfig, undefined, bulkSortArray));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((errors) => {
        onRejectedSubmit(errors);
      });
  };

  const onResend = () => api.post('/customer/otp/generate');
  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const handleAddBulkTransfer = useCallback(
    (values: IFormValues) => {
      setSubmitStatus(AxiosStatus.PENDING);
      const { sourceAccount } = values;
      const fromAccount = accounts.find((a) => a.accountNumber === sourceAccount);
      if (!fromAccount) {
        console.error('Invalid source account selected');
        // TODO: show prompt
      }
      const requestBody = {
        sourceAccount: fromAccount?.accountNumber,
        file: values.file[0],
      };
      api
        .postWithFiles('transfer/bulkUploadMobileMoney', requestBody, false, false)
        .then((data: IBulkUploadData) => {
          setMainData(data.bulkUploadData);
          setElevyState(data.elevy);
          if (data.code === 10) {
            setErrorMsg(data.description);
          }
          if (data.bulkUploadData?.length > 0) {
            setTableData(data.bulkUploadData);
            setTotalRows(data.bulkUploadData.length);
            const dataWithError = data.bulkUploadData.some((item) => item.code === 10);
            if (dataWithError) {
              setDisableMakeTransfer(true);
            } else {
              setDisableMakeTransfer(false);
            }
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setSubmitStatus(AxiosStatus.DEFAULT);
        });
    },
    [accounts],
  );

  useEffect(() => {
    const data = mainData;
    const indexOfLastPost = pageNumber * pageSize;
    const indexOfFirstPost = indexOfLastPost - pageSize;
    const currentData = data.slice(indexOfFirstPost, indexOfLastPost);
    setTableData(currentData);
  }, [mainData, pageNumber, pageSize]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const handleCancelBulk = () => {
    setTableData([]);
    mainForm.change('file', []);
  };

  const renderTableData = () => (
    <>
      <Table columns={columns} data={tableData} emptyTableMessage={emptyMessage} />
      <TablePagination
        count={Math.ceil(totalRows / pageSize)}
        page={pageNumber}
        pageSize={pageSize}
        totalCount={totalRows}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
      />
    </>
  );

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      const data = mainData.map((item) => ({
        beneficiaryName: item.beneficiaryName,
        beneficiaryWalletNumber: item.beneficiaryWalletNumber,
        netWork: item.netWork,
        walletName: item.walletName,
        amount: Number(item.amount),
        eleveyFee: item.eleveyFee,
        eleveyId: item.eleveyId,
        charge: item.charge,
        narration: item.narration,
        transactionType: 'A2W',
      }));
      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const requestBody = {
            sourceAccount: values.sourceAccount,
            transferData: JSON.stringify(data),
            password: otp,
            passwordType: 'OTP',
            saveBen: saveBeneficiary,
            totalElevyFee: elevyState,
          };
          api
            .post('transfer/manualMobileMoney', requestBody)
            .then((response: IMobileMoneyResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer failed. Unable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, mainData, elevyState],
  );

  const handleDeleteFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, form: FormType) => {
    e.stopPropagation();
    form.change('file', []);
    setTableData([]);
  };

  const subTitleItem = (
    <div>
      <p className={styles.subTitleItem}>
        Download the template below, complete it and upload to the section on the right
      </p>
      <a href="/excel_templates/MOMO_UPLOAD_TEMPLATE (4).xlsx" target="_parent" download>
        <Button type="button" className={styles.btnSubmit} theme={ButtonThemes.secondary} size={ButtonSizes.small}>
          <img src={download} alt="delete icon" />
          <span className={styles.downloadText}>Download Template</span>
        </Button>
      </a>
    </div>
  );

  const renderBulkUploadField = (values: IFormValues, form: FormType) => {
    if (Object.keys(values).length !== 0) {
      if (values.file && values.file.length > 0) {
        return (
          <>
            <div className={styles.valueContentContainer}>
              <div className={styles.nameAndSizeContainer}>
                <img src={excel} alt="excel icon" />
                <div>
                  <p className={styles.name}>{values.file[0] && values.file[0].name}</p>
                  <p className={styles.size}>{values.file[0] && getFileSize(values.file[0].size)}</p>
                </div>
              </div>
              <div
                className={styles.fileDeleteBtn}
                onClick={(e) => handleDeleteFile(e, form)}
                role="button"
                tabIndex={0}
                onKeyPress={noop}
              >
                <img src={trash} alt="delete icon" width="40" height="40" />
              </div>
            </div>
          </>
        );
      }
    }
    return renderBulkFileUpload(submitting);
  };
  return (
    <>
      {showMainForm && (
        <DashboardCard>
          <Form
            onSubmit={handleAddBulkTransfer}
            validate={validate}
            mutators={{ changeFieldValue, setFieldTouched }}
            render={({ handleSubmit, values, form, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  <FieldsLeveler label="Source Account">{renderTransferFieldsFrom(accounts, submitting)}</FieldsLeveler>
                  <FieldsLeveler label="Beneficiary List" subTitle={subTitleItem}>
                    {renderBulkUploadField(values, form)}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    <Button type="submit" fullWidth disabled={submitting || hasValidationErrors}>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Upload Forms'}
                    </Button>
                    <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
                      {simpleWarning}
                    </WarningModal>
                  </SubmitLeveler>
                  <OTPConfirmationModal
                    open={isConfirmationOpen}
                    data={previewData}
                    onClose={closeConfirmation}
                    onResend={onResend}
                    OTPPinError={OTPError}
                    onSubmit={onConfirm(values)}
                    submitStatus={submitStatus}
                    onSuccess={handleOnSuccess}
                    onRetry={onRetryConfirm}
                    successText={successMsg}
                    failureText={errorMsg}
                    askSaveBeneficiary
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? successMsg : (
                      <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                    )}
                  </StatusModal>
                  <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
                </form>
              );
            }}
          />
        </DashboardCard>
      )}
      <DashboardCard>
        {bulkTransfersState === 'adding' && tableData.length > 0 && (
          <>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} noArrow>
              Bulk Transfers List
            </NavigationTitle>
            {renderTableData()}
            <SubmitLeveler>
              <Button
                type="button"
                fullWidth
                disabled={!tableData.length || disableBulkTransfer}
                onClick={BulkTransfersConfirmation}
              >
                Make Transfer
              </Button>
            </SubmitLeveler>
            <SubmitLeveler>
              <div
                role="button"
                className={styles.cancelTransfer}
                onClick={handleCancelBulk}
                tabIndex={0}
                onKeyPress={noop}
              >
                Cancel Bulk Transfer
              </div>
            </SubmitLeveler>
          </>
        )}
      </DashboardCard>
    </>
  );
};

export default MMFileUpload;
